import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { CartItem, CartLevels } from '@open-tender/types'
import {
  formatDollars,
  hasGroupsBelowMin,
  makeMenuItemLookup,
  makeOrderItem,
  useBuilder,
} from '@open-tender/utils'
import {
  selectMenu,
  selectOrder,
  selectPointsProgram,
  showNotification,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { Body, Button, Checkmark, Heading } from 'components'
import { Minus, Plus } from 'components/icons'
import { sendModifierEvent } from 'app/analytics'
import {
  clearSlots,
  countFilledSlots,
  getAssortmentSlots,
  getSelectedOption,
  getSlotChanges,
  selectSlotOption,
} from 'utils/assortment'
import { getOptionDetailsFromLevels } from 'utils/helpers'
import AssortmentCustomize from './AssortmentCustomize'
import AssortmentSheet from './AssortmentSheet'
import {
  AssortmentContent,
  AssortmentDescription,
  AssortmentFooter,
  AssortmentFooterButton,
  AssortmentFooterButtons,
  AssortmentHeader,
  AssortmentPrice,
  AssortmentProgress,
  AssortmentQuantity,
  AssortmentQuantityControls,
  AssortmentSlot,
  AssortmentSlotActions,
  AssortmentSlotLabel,
  AssortmentSlotList,
  AssortmentSlotMain,
  AssortmentSlotMarker,
  AssortmentSlotName,
  AssortmentSlotSummary,
  AssortmentSlotText,
  AssortmentSlotWarning,
  AssortmentTitle,
  AssortmentTitleRow,
} from './MenuItemAssortment.styled'

type View = { type: 'slots' } | { type: 'customize'; slotId: number }

// A fresh copy of the menu item, used to restore a swapped-in option to its
// default nested choices (the edited cart item can't be used for that).
const usePristineItem = (item: CartItem): CartItem => {
  const { categories, soldOut } = useAppSelector(selectMenu)
  const { requestedAt } = useAppSelector(selectOrder)
  const hasPoints = !!useAppSelector(selectPointsProgram)
  const lookup = useMemo(() => makeMenuItemLookup(categories), [categories])
  const menuItem = lookup[item.id]
  return useMemo(
    () =>
      menuItem
        ? (makeOrderItem(
            menuItem,
            undefined,
            soldOut,
            undefined,
            hasPoints,
            requestedAt
          ) as CartItem)
        : item,
    [menuItem, soldOut, hasPoints, requestedAt, item]
  )
}

const AssortmentBuilder = ({
  seed,
  pristine,
  view,
  setView,
  sheetSlotId,
  setSheetSlotId,
  focusSlotId,
  setFocusSlotId,
  reseed,
  addItem,
  topContent,
}: {
  seed: CartItem
  pristine: CartItem
  view: View
  setView: (view: View) => void
  sheetSlotId: number | null
  setSheetSlotId: (id: number | null) => void
  focusSlotId: number | null
  setFocusSlotId: (id: number | null) => void
  reseed: (item: CartItem) => void
  addItem: (item: CartItem) => void
  topContent?: ReactNode
}) => {
  const dispatch = useAppDispatch()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const builder = useBuilder(seed)
  const { item: builtItem, increment, decrement } = builder
  const slots = getAssortmentSlots(builtItem) ?? []
  const filled = countFilledSlots(slots)
  const remaining = slots.length - filled
  const incomplete = hasGroupsBelowMin(null, builtItem.groups)
  const sheetSlot = slots.find((slot) => slot.id === sheetSlotId) ?? null
  const customizeSlot =
    view.type === 'customize'
      ? slots.find((slot) => slot.id === view.slotId) ?? null
      : null
  const customizeOption = customizeSlot
    ? getSelectedOption(customizeSlot)
    : null

  // Each screen starts at the top, e.g. after Done on a long customize list.
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 })
  }, [view.type])

  // Hand focus back to the slot card after a swap, clear or Done.
  useEffect(() => {
    if (focusSlotId === null || view.type !== 'slots') return
    document
      .querySelector<HTMLElement>(`#assortment-slot-${focusSlotId} button`)
      ?.focus({ preventScroll: true })
    setFocusSlotId(null)
  }, [focusSlotId, view.type, setFocusSlotId])

  const toggle = (levels: CartLevels) => {
    const updated = builder.toggleOption(levels)
    const modifier = getOptionDetailsFromLevels(updated.groups, levels)
    sendModifierEvent({ type: 'toggle', modifier, item: updated })
  }

  const increase = (levels: CartLevels) => {
    const updated = builder.incrementOption(levels)
    const modifier = getOptionDetailsFromLevels(updated.groups, levels)
    sendModifierEvent({ type: 'increment', modifier, item: updated })
  }

  const decrease = (levels: CartLevels) => {
    const updated = builder.decrementOption(levels)
    const modifier = getOptionDetailsFromLevels(updated.groups, levels)
    sendModifierEvent({ type: 'decrement', modifier, item: updated })
  }

  const chooseOption = (slotId: number, optionId: number) => {
    const current = slots.find((slot) => slot.id === slotId)
    setSheetSlotId(null)
    setFocusSlotId(slotId)
    if (
      getSelectedOption(current as NonNullable<typeof current>)?.id === optionId
    )
      return
    const next = selectSlotOption(builtItem, pristine, slotId, optionId)
    const levels: CartLevels = [[slotId, optionId]]
    const modifier = getOptionDetailsFromLevels(next.groups, levels)
    sendModifierEvent({ type: 'toggle', modifier, item: next })
    reseed(next)
  }

  const clearAll = () => {
    setFocusSlotId(slots[0]?.id ?? null)
    reseed(clearSlots(builtItem))
  }

  const closeCustomize = () => {
    if (view.type === 'customize') setFocusSlotId(view.slotId)
    setView({ type: 'slots' })
  }

  const scrollToRequired = () => {
    const node = contentRef.current?.querySelector<HTMLElement>('.required')
    node?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    node?.focus({ preventScroll: true })
  }

  const onDone = () => {
    if (
      customizeOption &&
      hasGroupsBelowMin(customizeOption, customizeOption.groups)
    ) {
      dispatch(showNotification('Please select all required items'))
      scrollToRequired()
      return
    }
    closeCustomize()
  }

  const onAdd = () => {
    if (!incomplete) return addItem(builtItem)
    const slot = slots.find((s) => {
      const option = getSelectedOption(s)
      return option && hasGroupsBelowMin(option, option.groups)
    })
    dispatch(showNotification('Please select all required items'))
    if (slot) setView({ type: 'customize', slotId: slot.id })
  }

  const priceLabel = formatDollars(`${builtItem.totalPrice}`)
  const isCustomize = !!(customizeSlot && customizeOption)

  return (
    <>
      <AssortmentContent id="menu-item-content" ref={contentRef}>
        {!isCustomize ? topContent : null}
        {isCustomize && customizeSlot && customizeOption ? (
          <AssortmentCustomize
            slot={customizeSlot}
            option={customizeOption}
            onBack={closeCustomize}
            toggleOption={toggle}
            incrementOption={increase}
            decrementOption={decrease}
            setOptionQuantity={builder.setOptionQuantity}
          />
        ) : (
          <>
            <AssortmentHeader>
              <AssortmentTitleRow>
                <AssortmentTitle as="h1" size="xBig">
                  {builtItem.name}
                </AssortmentTitle>
                <AssortmentPrice>
                  <Heading size="big">{priceLabel}</Heading>
                </AssortmentPrice>
              </AssortmentTitleRow>
              <AssortmentProgress aria-live="polite">
                {filled} of {slots.length} selected
              </AssortmentProgress>
              {builtItem.description ? (
                <AssortmentDescription as="p">
                  {builtItem.description}
                </AssortmentDescription>
              ) : null}
            </AssortmentHeader>
            <AssortmentSlotList>
              {slots.map((slot, index) => {
                const option = getSelectedOption(slot)
                const changes = getSlotChanges(option)
                const attention = !!(
                  option && hasGroupsBelowMin(option, option.groups)
                )
                return (
                  <AssortmentSlot
                    key={slot.id}
                    id={`assortment-slot-${slot.id}`}
                    filled={!!option}
                    attention={attention}
                  >
                    <AssortmentSlotMain>
                      <AssortmentSlotMarker
                        filled={!!option}
                        aria-hidden="true"
                      >
                        {option ? <Checkmark /> : index + 1}
                      </AssortmentSlotMarker>
                      <AssortmentSlotText>
                        {option ? (
                          <AssortmentSlotLabel as="div">
                            {slot.name}
                          </AssortmentSlotLabel>
                        ) : null}
                        <AssortmentSlotName size="big" muted={!option}>
                          {option ? option.name : slot.name}
                        </AssortmentSlotName>
                        {changes.length > 0 ? (
                          <AssortmentSlotSummary as="div">
                            {changes.join(', ')}
                          </AssortmentSlotSummary>
                        ) : null}
                        {attention ? (
                          <AssortmentSlotWarning as="div">
                            Needs your attention
                          </AssortmentSlotWarning>
                        ) : null}
                      </AssortmentSlotText>
                    </AssortmentSlotMain>
                    <AssortmentSlotActions>
                      {option ? (
                        <>
                          <Button
                            onClick={() => setSheetSlotId(slot.id)}
                            size="small"
                            label={`Swap ${slot.name}`}
                          >
                            Swap
                          </Button>
                          <Button
                            onClick={() =>
                              setView({ type: 'customize', slotId: slot.id })
                            }
                            size="small"
                            color="secondary"
                            label={`Customize ${option.name} for ${slot.name}`}
                          >
                            Customize
                          </Button>
                        </>
                      ) : (
                        <Button
                          onClick={() => setSheetSlotId(slot.id)}
                          size="small"
                          label={`Select ${slot.name}`}
                        >
                          Select
                        </Button>
                      )}
                    </AssortmentSlotActions>
                  </AssortmentSlot>
                )
              })}
            </AssortmentSlotList>
            <AssortmentQuantity>
              <Body>Quantity</Body>
              <AssortmentQuantityControls>
                <Button
                  onClick={decrement}
                  size="small"
                  color="primary"
                  label="Decrease Quantity"
                >
                  <Minus size={16} />
                </Button>
                <Heading size="big" aria-live="polite">
                  {builtItem.quantity}
                </Heading>
                <Button
                  onClick={increment}
                  size="small"
                  color="primary"
                  label="Increase Quantity"
                >
                  <Plus size={16} />
                </Button>
              </AssortmentQuantityControls>
            </AssortmentQuantity>
          </>
        )}
      </AssortmentContent>
      <AssortmentFooter>
        {isCustomize ? (
          <AssortmentFooterButtons>
            <AssortmentFooterButton>
              <Button onClick={onDone} size="big">
                Done
              </Button>
            </AssortmentFooterButton>
          </AssortmentFooterButtons>
        ) : (
          <AssortmentFooterButtons>
            <AssortmentFooterButton grow={1.1}>
              <Button
                onClick={clearAll}
                size="big"
                color="secondary"
                disabled={filled === 0}
              >
                {isMobile ? 'Clear' : 'Clear Selections'}
              </Button>
            </AssortmentFooterButton>
            <AssortmentFooterButton grow={1.2}>
              <Button
                onClick={onAdd}
                size="big"
                disabled={remaining > 0 || builtItem.quantity === 0}
              >
                {remaining > 0
                  ? `Select ${remaining} More`
                  : `${isMobile ? 'Add' : 'Add To Order'} — ${priceLabel}`}
              </Button>
            </AssortmentFooterButton>
          </AssortmentFooterButtons>
        )}
      </AssortmentFooter>
      {sheetSlot ? (
        <AssortmentSheet
          group={sheetSlot}
          onSelect={(optionId) => chooseOption(sheetSlot.id, optionId)}
          onClose={() => {
            setFocusSlotId(sheetSlot.id)
            setSheetSlotId(null)
          }}
        />
      ) : null}
    </>
  )
}

const MenuItemAssortment = ({
  item,
  addItem,
  topContent,
}: {
  item: CartItem
  addItem: (item: CartItem) => void
  topContent?: ReactNode
}) => {
  const pristine = usePristineItem(item)
  const [seed, setSeed] = useState<CartItem>(item)
  const [version, setVersion] = useState(0)
  const [view, setView] = useState<View>({ type: 'slots' })
  const [sheetSlotId, setSheetSlotId] = useState<number | null>(null)
  const [focusSlotId, setFocusSlotId] = useState<number | null>(null)

  // useBuilder only reads its initial item, so changes it can't express
  // (clearing, swapping with a reset) re-mount it with a new seed.
  const reseed = (next: CartItem) => {
    setSeed(next)
    setVersion((v) => v + 1)
  }

  return (
    <AssortmentBuilder
      key={version}
      seed={seed}
      pristine={pristine}
      view={view}
      setView={setView}
      sheetSlotId={sheetSlotId}
      setSheetSlotId={setSheetSlotId}
      focusSlotId={focusSlotId}
      setFocusSlotId={setFocusSlotId}
      reseed={reseed}
      addItem={addItem}
      topContent={topContent}
    />
  )
}

export default MenuItemAssortment
